import type { StatMod } from './types';

/** Always-on character trait; `mods` join the stat stack in `Sim.recomputeStats()`. */
export interface CharacterPassive {
  name: string;
  description: string;
  mods: readonly StatMod[];
}

/**
 * Playable roster shared by progression, deterministic simulation, and UI.
 * Each `passive` is plain stat data (meta → passive → equipment → run), so it
 * stays replay- and co-op-deterministic.
 */
export const CHARACTERS = [
  {
    id: 'knight',
    name: 'Knight',
    model: 'models/kaykit/characters/Knight.glb',
    tagline: 'The starting timewalker. Armored, steady, and hard to pin down.',
    ability: { name: 'Dash', description: 'Burst through danger with brief invulnerability.', cooldown: 1.2, charges: 1 },
    passive: { name: 'Bulwark', description: '+2 armor and +10% max HP.', mods: [{ stat: 'armor', add: 2 }, { stat: 'maxHp', mul: 1.1 }] },
    unlockAfterEra: -1,
  },
  {
    id: 'barbarian',
    name: 'Barbarian',
    model: 'models/kaykit/characters/Barbarian.glb',
    tagline: 'A powerful silhouette built for controlling the space around them.',
    ability: { name: 'War Cry', description: 'Damage and hurl back nearby enemies.', cooldown: 5, charges: 1 },
    passive: { name: 'Bloodlust', description: 'Heal for 3% of damage dealt and +15% max HP, but −10% fire rate.', mods: [{ stat: 'lifesteal', add: 0.03 }, { stat: 'maxHp', mul: 1.15 }, { stat: 'fireRate', mul: 0.9 }] },
    unlockAfterEra: 0,
  },
  {
    id: 'mage',
    name: 'Mage',
    model: 'models/kaykit/characters/Mage.glb',
    tagline: 'A scholar of unstable timelines and elemental possibilities.',
    ability: { name: 'Arcane Nova', description: 'Release a ring of piercing arcane bolts.', cooldown: 4.5, charges: 1 },
    passive: {
      name: 'Attunement',
      description: 'Hits have a 10% chance to arc lightning to 1 more enemy, and every elemental proc chance is ×1.35.',
      mods: [
        { stat: 'lightningChance', add: 0.1 }, { stat: 'lightningChains', add: 1 },
        { stat: 'fireChance', mul: 1.35 }, { stat: 'frostChance', mul: 1.35 }, { stat: 'lightningChance', mul: 1.35 }, { stat: 'temporalChance', mul: 1.35 },
      ],
    },
    unlockAfterEra: 1,
  },
  {
    id: 'ranger',
    name: 'Ranger',
    model: 'models/kaykit/characters/Ranger.glb',
    tagline: 'A precise traveler who reads the battlefield before committing.',
    ability: { name: 'Piercing Volley', description: 'Fire a focused fan through the nearest threat.', cooldown: 3.5, charges: 1 },
    passive: { name: 'Eagle Eye', description: '+25% range, +7% crit chance and +15% projectile speed.', mods: [{ stat: 'range', mul: 1.25 }, { stat: 'critChance', add: 0.07 }, { stat: 'projectileSpeed', mul: 1.15 }] },
    unlockAfterEra: 2,
  },
  {
    id: 'rogue',
    name: 'Rogue',
    model: 'models/kaykit/characters/Rogue.glb',
    tagline: 'A hooded opportunist at home between bullets and broken seconds.',
    ability: { name: 'Phase Step', description: 'Blink forward and erase enemy bullets at the exit.', cooldown: 4, charges: 1 },
    // 0.2 + Walking Fire 0.4 + Gunslinger 0.4 tops out at exactly full rate while moving.
    passive: { name: 'Run and Gun', description: 'Fire at 20% rate while moving and +8% move speed, but −10% max HP.', mods: [{ stat: 'movingFireMult', add: 0.2 }, { stat: 'moveSpeed', mul: 1.08 }, { stat: 'maxHp', mul: 0.9 }] },
    unlockAfterEra: 3,
  },
] as const satisfies readonly { id: string; passive: CharacterPassive; [key: string]: unknown }[];

export type CharacterId = (typeof CHARACTERS)[number]['id'];
export type CharacterDef = (typeof CHARACTERS)[number];
export const DEFAULT_CHARACTER: CharacterId = 'knight';

export function characterId(value: unknown): CharacterId {
  return typeof value === 'string' && CHARACTERS.some((c) => c.id === value) ? (value as CharacterId) : DEFAULT_CHARACTER;
}

export function characterDef(value: unknown): CharacterDef {
  return CHARACTERS.find((c) => c.id === characterId(value)) ?? CHARACTERS[0];
}

export function characterAtOffset(selected: unknown, offset: number): CharacterDef {
  const start = CHARACTERS.findIndex((c) => c.id === characterId(selected));
  return CHARACTERS[(start + offset) % CHARACTERS.length];
}

export function characterUnlockLabel(character: CharacterDef): string {
  return character.unlockAfterEra < 0 ? 'Available from the start' : `Defeat the Era ${character.unlockAfterEra + 1} boss`;
}
